import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {ProjectsDashboardDb} from '@assets/projects-dashboard';
import {environment} from '@env/environment';
import {catchError, map} from 'rxjs/operators';
import {
    ApplicationReportItem,
    ApplicationWithoutScheduleReportItem,
    DataAccessIntegrationDetails,
    DataAccessIssueReportItem,
    DataAccessProgress,
    ETXIncident,
    ETXIncidentsSummary,
    ETXFailedSessionsSummary,
    ExpireSoonIssueReportItem,
    IntegrationReportItem,
    InternalSyncIssueReportItem,
    IssueReportItem,
    ProblemType,
    SchoolMappingIssueReportItem,
    SSOPortalIssueReportItem,
    SSOPortalIssuesFilters,
    StatWidgetData,
    SynchronizationIssueReportItem,
    SSOPortalWidgetDataItem,
    OrgsByRosterDataSizeReport
} from '@app/views/dashboard/dashboard.model';
import {IntegrationState} from '@app/views/admin/application-settings/models/integration-state.model';
import {ETXTransactionHistoryItemContent} from '@app/views/admin/components/transactions-history/transactions-history-timeline/models/transaction-history-item.model';

@Injectable()
export class ProjectsDashboardService implements Resolve<any> {
    projects: any[];
    widgets: any;

    constructor(
        private http: HttpClient
    ) {
    }

    /**
     * Resolve
     * @param route
     * @param state
     * @returns
     */
    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Promise<any> | any {

        return new Promise<void>((resolve, reject) => {

            Promise.all([
                // this.getProjects(),
                this.getWidgets()
            ]).then(
                () => {
                    resolve();
                },
                reject
            );
        });
    }

    getProjects(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get('api/projects-dashboard-projects')
                .subscribe((response: any) => {
                    this.projects = response;
                    resolve(response);
                }, reject);
        });
    }

    getWidgets(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.widgets = ProjectsDashboardDb.widgets;
            resolve(this.widgets);
        });
    }

    getProviderDashboardStatWidgetsTemplate(): StatWidgetData[] {
        return [
            new StatWidgetData('Schools', '', 'School', 'schools'),
            new StatWidgetData('Students', '', 'Student', 'students'),
            new StatWidgetData('Contacts', '', 'Contact', 'contacts'),
            new StatWidgetData('Teachers', '', 'Staff', 'staffs'),
            new StatWidgetData('Classes', '', 'Roster', 'rosters'),
        ];
    }


    getSynchronizationsReport(): Observable<SynchronizationIssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/synchronizationsReport`;
        return this.http
            .get<SynchronizationIssueReportItem[]>(url)
            .pipe(
                map((reportItems: SynchronizationIssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: SynchronizationIssueReportItem) =>
                            new SynchronizationIssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                item.problemType,
                                // new Date(item.creationTs)
                                item.creationTs
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getSchoolsMappingReport(): Observable<SchoolMappingIssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/schoolsMappingReport`;
        return this.http
            .get<SchoolMappingIssueReportItem[]>(url)
            .pipe(
                map((reportItems: SchoolMappingIssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: SchoolMappingIssueReportItem) =>
                            new SchoolMappingIssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.SCHOOL_MAPPING,
                                item.creationTs,
                                item.vanishedFromMapping,
                                item.appearedInDs,
                                item.notMapped
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getApplicationsWithoutScheduleReport(): Observable<ApplicationWithoutScheduleReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/applicationsWithoutSchedule`;
        return this.http
            .get<ApplicationWithoutScheduleReportItem[]>(url)
            .pipe(
                map((reportItems: ApplicationWithoutScheduleReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ApplicationWithoutScheduleReportItem) =>
                            new ApplicationWithoutScheduleReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.APPLICATION_WITHOUT_SCHEDULE
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getApplicationsInNotProcessedStateReport(): Observable<ApplicationReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/applicationsInNotProcessedState`;
        return this.http
            .get<ApplicationReportItem[]>(url)
            .pipe(
                map((reportItems: ApplicationReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ApplicationReportItem) =>
                            new ApplicationReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                null,
                                item.creationTs,
                                item.state
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getChangesPendingApprovalBySysAdmReport(): Observable<IssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/allEnormousChangesReport`;

        return this.http
            .get<IssueReportItem[]>(url)
            .pipe(
                map((reportItems: IssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: IssueReportItem) =>
                            new IssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.CHANGES_PENDING_APPROVAL_BY_SYSADMIN,
                                item.creationTs,
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getPreviewModeApplicationsReport(): Observable<ApplicationReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/previewModeApplicationsReport`;

        return this.http
            .get<ApplicationReportItem[]>(url)
            .pipe(
                map((reportItems: ApplicationReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ApplicationReportItem) =>
                            new ApplicationReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.PREVIEW_MODE,
                                null,
                                item.state
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getSpecialOfferReport(): Observable<IssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/specialOffersActivating`;

        return this.http
            .get<IssueReportItem[]>(url)
            .pipe(
                map((reportItems: IssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ApplicationReportItem) =>
                            new IssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.SPECIAL_OFFER_ACTIVATION,
                                item.creationTs
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getCreatedApplicationsReport(): Observable<IssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/applicationsCreated`;

        return this.http
            .get<IssueReportItem[]>(url)
            .pipe(
                map((reportItems: IssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ApplicationReportItem) =>
                            new IssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.CREATED_APP,
                                item.creationTs
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getUpdatedApplicationsReport(): Observable<IssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/applicationsUpdated`;

        return this.http
            .get<IssueReportItem[]>(url)
            .pipe(
                map((reportItems: IssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ApplicationReportItem) =>
                            new IssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.UPDATED_APP,
                                item.creationTs
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getExpireSoonApplicationsReport(): Observable<ExpireSoonIssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/expireSoonIntegrations`;

        return this.http
            .get<ExpireSoonIssueReportItem[]>(url)
            .pipe(
                map((reportItems: ExpireSoonIssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ExpireSoonIssueReportItem) =>
                            new ExpireSoonIssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.EXPIRE_SOON_INTEGRATION,
                                item.expirationTs
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getInvalidSSOApplicationsReport(): Observable<IssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/ssoApplicationWithoutSsoService`;

        return this.http
            .get<IssueReportItem[]>(url)
            .pipe(
                map((reportItems: IssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ApplicationReportItem) =>
                            new IssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.INVALID_SSO_APP,
                                item.creationTs
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }


    setProblemTypeForInactiveApps(reportItems: ApplicationReportItem[]): ApplicationReportItem[] {
        const result: ApplicationReportItem[] = [];
        reportItems.forEach((item: ApplicationReportItem) => {
            switch (item.state) {
                case IntegrationState.PENDING:
                    item.problemType = ProblemType.PENDING_APPLICATION;
                    break;

                case IntegrationState.REJECTED_PP:
                    item.problemType = ProblemType.REJECTED_PP_APPLICATION;
                    break;

                case IntegrationState.EXPIRED:
                    item.problemType = ProblemType.EXPIRED_APPLICATION;
                    break;

                case IntegrationState.SUSPENDED:
                    item.problemType = ProblemType.SUSPENDED_APPLICATION;
                    break;

                case IntegrationState.EXPIRED_AND_SUSPENDED:
                    result.push(new ApplicationReportItem(
                        item.organizationGuid,
                        item.applicationGuid,
                        item.defaultApplicationGuid,
                        ProblemType.EXPIRED_APPLICATION,
                        item.creationTs,
                        item.state
                    ));
                    item.problemType = ProblemType.SUSPENDED_APPLICATION;
                    break;


                case IntegrationState.SUSPENDED_DUE_SCHOOL_YEAR_ROLL_OVER_PERIOD:
                    item.problemType = ProblemType.SUSPENDED_DUE_SCHOOL_YEAR_ROLL_OVER_PERIOD;
                    break;

                case IntegrationState.SIGNIFICANT_CHANGES_DETECTED_IN_OTHER:
                    item.problemType = ProblemType.DATA_VERIFICATION;
                    break;

                case IntegrationState.SIGNIFICANT_CHANGES_DETECTED_IN_CURRENT:
                case IntegrationState.SIGNIFICANT_CHANGES_DETECTED_IN_ACCESS:
                case IntegrationState.SIGNIFICANT_CHANGES_DETECTED_IN_ACCESS_AND_SYNC:
                    item.problemType = ProblemType.CHANGES_PENDING_APPROVAL;
                    break;
            }
            result.push(item);
        });
        return result;
    }

    getDataAccessReport(): Observable<DataAccessIssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/integrationsWithDataAccessProblem`;
        return this.http
            .get<DataAccessIssueReportItem[]>(url)
            .pipe(
                map((reportItems: DataAccessIssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: DataAccessIssueReportItem) =>
                            new DataAccessIssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.DATA_ACCESS,
                                item.creationTs,
                                item.guid,
                                item.integrationId
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getIntegrationsWithoutChangesAfterDataSync(): Observable<IssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/integrationsWithoutChangesAfterDataSync`;

        return this.http
            .get<IssueReportItem[]>(url)
            .pipe(
                map((reportItems: IssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ApplicationReportItem) =>
                            new IssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.NO_CHANGE_AFTER_DATA_SYNC,
                                item.creationTs
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getInternalSyncConnectReport(): Observable<InternalSyncIssueReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/failedTransactionMessages`;
        return this.http
            .get<InternalSyncIssueReportItem[]>(url)
            .pipe(
                map((reportItems: InternalSyncIssueReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: InternalSyncIssueReportItem) =>
                            new InternalSyncIssueReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                ProblemType.FAILED_TRANSACTION_MESSAGE,
                                // new Date(item.creationTs)
                                item.creationTs,
                                item.transactionId
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getInternalSyncETXReport() {
        const formData = new FormData();
        formData.append('status', 'FAILED');
        const url = `${environment.etxBaseURI}/sync/processing/session/list`;

        return this.http
            .post<ETXTransactionHistoryItemContent[]>(url, formData)
            .pipe(
                map((failedSessions: ETXTransactionHistoryItemContent[]) =>
                    !failedSessions
                        ? null
                        : failedSessions
                            .filter((item: ETXTransactionHistoryItemContent) => item.status === 'FAILED')
                            .map((item: ETXTransactionHistoryItemContent) =>
                                new InternalSyncIssueReportItem(
                                    item.districtGuid,
                                    null,
                                    null,
                                    ProblemType.FAILED_TRANSACTION_MESSAGE,
                                    item.timepointStart ? new Date(item.timepointStart) : null,
                                    item.sessionId,
                                    item.tenant
                                    // item.status
                                    // item.timepointFinish
                                )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getInternalSyncETXSummary(): Observable<ETXFailedSessionsSummary> {
        const url = `${environment.etxBaseURI}/sync/processing/session/failed/count`;
        return this.http
            .get<ETXFailedSessionsSummary>(url)
            .pipe(
                catchError(this.handleError)
            );
    }

    getIncidentsSummary(): Observable<ETXIncidentsSummary> {
        const url = `${environment.etxBaseURI}/services/incidents/dashboard/summary`;
        return this.http
            .get<ETXIncidentsSummary>(url)
            .pipe(
                catchError(this.handleError)
            );
    }

    getDataAccessConsistencyCheckProcessProgress(): Observable<number> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/data-access-consistency-progress`;
        return this.http
            .get<number>(url)
            .pipe(
                catchError(this.handleError)
            );
    }


    getAllDataAccessProcessesProgress(): Observable<DataAccessProgress[]> {
        const url = `${environment.datahubBaseURI}/api/v2/rosterAccess/access-in-progress`;
        return this.http
            .get<DataAccessProgress[]>(url)
            .pipe(
                catchError(this.handleError)
            );
    }

    getDataAccessProblemIntegrationDetails(integrationId: string): Observable<DataAccessIntegrationDetails> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/dataAccessProblemIntegrationDetails/${integrationId}`;
        return this.http
            .get<DataAccessIntegrationDetails>(url)
            .pipe(
                catchError(this.handleError)
            );
    }

    runDataAccessCheckingProcess(integrationId: string): Observable<DataAccessIntegrationDetails> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/check-data-access-consistency/${integrationId}`;
        return this.http
            .post<DataAccessIntegrationDetails>(url, null)
            .pipe(
                catchError(this.handleError)
            );
    }

    refreshDataAccess(integrationId: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/refreshAccess/${integrationId} `;
        return this.http
            .post<any>(url, null)
            .pipe(
                catchError(this.handleError)
            );
    }

    disableSchoolMappingNotification(organizationGuid: string, applicationGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/disableNotification/${organizationGuid}/${applicationGuid}`;
        return this.http.post(url, null)
            .pipe(catchError(this.handleError));
    }

    disableBrokenConnectionNotification(organizationGuid: string, applicationGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/disableBrokenConnectionNotification/${organizationGuid}/${applicationGuid}`;
        return this.http.post(url, null)
            .pipe(catchError(this.handleError));
    }

    disableFailedSyncProblemNotification(organizationGuid: string, applicationGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/disableFailedSyncProblemNotification/${organizationGuid}/${applicationGuid}`;
        return this.http.post(url, null)
            .pipe(catchError(this.handleError));
    }

    disableSynchronizationProblemNotification(organizationGuid: string, applicationGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/disableSynchronizationProblemNotification/${organizationGuid}/${applicationGuid}`;
        return this.http.post(url, null)
            .pipe(catchError(this.handleError));
    }

    disableSynchronizationWarnProblemNotification(organizationGuid: string, applicationGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/disableSynchronizationWarnProblemNotification/${organizationGuid}/${applicationGuid}`;
        return this.http.post(url, null)
            .pipe(catchError(this.handleError));
    }

    sendSpecialOfferEmailNotification(organizationGuid: string, applicationGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/sendSpecialOfferActivated/${organizationGuid}/${applicationGuid}`;
        return this.http.post(url, null)
            .pipe(catchError(this.handleError));
    }

    removeIssueFromReports(issueReports: IssueReportItem[], disabledIssue: IssueReportItem): IssueReportItem[] {
        const isDisabledSchoolMappingIssue = disabledIssue.problemType == ProblemType.SCHOOL_MAPPING
            || disabledIssue.problemType == ProblemType.SCHOOL_MAPPING_WITH_VANISHED_SCHOOL;

        return (issueReports || []).filter((issue: IssueReportItem) => {
            const isSchoolMappingIssue = issue.problemType == ProblemType.SCHOOL_MAPPING
                || issue.problemType == ProblemType.SCHOOL_MAPPING_WITH_VANISHED_SCHOOL;

            return !(issue.organizationGuid == disabledIssue.organizationGuid &&
                issue.applicationGuid == disabledIssue.applicationGuid && (
                !isDisabledSchoolMappingIssue && issue.problemType == disabledIssue.problemType ||
                    isDisabledSchoolMappingIssue && isSchoolMappingIssue
            )
            );
        });
    }

    getIntegrationsReport(): Observable<IntegrationReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/dashboard/getIntegrations`;

        return this.http
            .get<IntegrationReportItem[]>(url)
            .pipe(
                map((reportItems: IntegrationReportItem[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: IntegrationReportItem) =>
                            new IntegrationReportItem(
                                item.organizationGuid,
                                item.applicationGuid,
                                item.defaultApplicationGuid,
                                item.integrationId,
                                item.mode,
                                item.state,
                                item.defaultSchemaName,
                                item.creationTs,
                                item.notEduToneDname
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getSSOPortalIssues(filters: SSOPortalIssuesFilters = null, sortDirection = 'asc', pageNumber = 0, pageSize = 15): Observable<SSOPortalIssueReportItem[]> {
        const url = `${environment.etxBaseURI}/services/incidents/dashboard/authentication`;
        let params = new HttpParams()
            // .set('sortDirection', sortDirection)
            .set('pageNumber', pageNumber.toString())
            .set('pageSize', pageSize.toString());
        if (filters) {
            const paramValues = new Map<string, string>([
                ['type', SSOPortalIssueReportItem.getETXIncidentType(filters?.problemType)],
                ['host', filters?.host],
                ['districtGuid', filters?.districtGuid],
                ['schoolGuid', filters?.schoolGuid],
                ['appId', filters?.appGuid],
                ['idp', filters?.idp],
                ['username', filters?.username],
                ['userGuid', filters?.userGuid],
                ['userRole', filters?.userRole],
                ['incidentType', filters?.errorType],
                ['stacktrace', filters?.errorMessage],
                ['startDate', filters?.startDate ? filters.startDate.getTime().toString() : ''],
                ['endDate', filters?.endDate ? filters.endDate.getTime().toString() : '']
            ]);
            paramValues.forEach((value: string, param: string) => {
                if (value) {
                    params = params.set(param, value);
                }
            });
        }
        return this.http
            .get<ETXIncident[]>(url, {params: params})
            .pipe(
                map((reportItems: ETXIncident[]) =>
                    !reportItems
                        ? null
                        : reportItems.map((item: ETXIncident) =>
                            new SSOPortalIssueReportItem(
                                item.districtGuid,
                                item.appId,
                                item.appId,
                                SSOPortalIssueReportItem.getProblemType(item.type),
                                item.date ? new Date(item.date) : null,
                                item.idp,
                                item.schoolGuid,
                                item.schoolName,
                                item.host,
                                item.userGuid,
                                item.username,
                                item.userRole,
                                item.incidentType,
                                item.stacktrace,
                                item.requestUrl,
                                item.userAgent,
                                item.totalPagesCount,
                                item.totalRecordsCount
                            )
                        )
                ),
                catchError(this.handleError)
            );
    }

    getTopSSOPortalIssues(
        incidentTypeFilter: string,
        byFilter: string,
        districtGuid: string,
        startDate: Date = null,
        endDate: Date = null,
        limit: number = 5
    ): Observable<SSOPortalWidgetDataItem[]> {
        const url = `${environment.etxBaseURI}/services/incidents/dashboard/${incidentTypeFilter}/${byFilter}?`;
        const params = [`limit=${limit}`];
        if (districtGuid) {
            params.push(`districtGuid=${districtGuid}`);
        }
        if (startDate) {
            params.push(`startDate=${startDate.getTime().toString()}`);
        }
        if (endDate) {
            params.push(`endDate=${endDate.getTime().toString()}`);
        }
        return this.http
            .get<SSOPortalWidgetDataItem[]>(url + params.join('&'))
            .pipe(
                catchError(this.handleError)
            );
    }

    getStats(offset: number, limit?: number, districtGuid?: string): Observable<OrgsByRosterDataSizeReport[]> {
        const params: any = {
            offset: 0,
            limit: 10000,
            sortField: 'total',
            orderBy: 'desc'
        };
        if (districtGuid) {
            params.districtGuid = districtGuid;
        }

        const args = Object.keys(params)
            .filter((paramName: string) => params[paramName] !== null)
            .map((paramName: string) => `${paramName}=${params[paramName]}`)
            .join('&');

        const url = `${environment.datahubBaseURI}/api/v2/stats?${args}`;

        return this.http
            .get<OrgsByRosterDataSizeReport[]>(url)
            .pipe(
                map((orgsByRosterDataSizeReport: any) => orgsByRosterDataSizeReport),
                catchError(this.handleError)
            );
    }

    getStatsCountByPeriod(): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/stats/statsCountByPeriod`;

        return this.http
            .get<any>(url)
            .pipe(
                map((response: any) =>
                    response
                ),
                catchError(this.handleError)
            );
    }

    getLastStatsCount(): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/stats/lastStatsCount`;

        return this.http
            .get<any>(url)
            .pipe(
                map((response: any) =>
                    response
                ),
                catchError(this.handleError)
            );
    }


    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.message}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }
}


